// src/components/CampaignDashboard.jsx

import {
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";

import {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { supabase } from "../utils/supabaseClient";

import { useAuth } from "../context/useAuth";
import useBusinessGuard from "../hooks/useBusinessGuard";

import DashboardSidebar from "./DashboardSidebar";
import LogoutButton from "./LogoutButton";
import RedemptionToastListener from "./RedemptionToastListener";
import NotificationToastListener from "./NotificationToastListener";
import CampaignIntelModules from "./CampaignIntelModules";
import DispatchModules from "./DispatchModules";
import SlotCountWatcher from "./SlotCountWatcher";
import CountdownWatcher from "./CountdownWatcher";
import PostcodeCouponsMap from "./PostcodeCouponsMap";
import BrandLogo from "./BrandLogo";

const isDev = import.meta.env.DEV;

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

export default function CampaignDashboard() {
  const { user } = useAuth() || {};

  const {
    profile,
    businessIdInt,
    authId,
    businessRow,
    loading,
  } = useBusinessGuard();

  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [
    regionOverride,
    setRegionOverride,
  ] = useState(null);

  const [
    selectedFlyer,
    setSelectedFlyer,
  ] = useState(null);

  const [
    slotCount,
    setSlotCount,
  ] = useState(0);

  const [
    sidebarOpen,
    setSidebarOpen,
  ] = useState(false);

  const [
    flyerLoading,
    setFlyerLoading,
  ] = useState(false);

  const upgraded =
    params.get("upgraded");

  const activeRegion =
    regionOverride
      ? String(regionOverride)
          .replace("_", "")
          .toUpperCase()
      : null;

  // -------------------------------------------------------
  // Debug: dashboard mounted
  // -------------------------------------------------------
  useEffect(() => {
    if (!isDev) return;

    console.log(
      "[CampaignDashboard] mounted"
    );
  }, []);

  // -------------------------------------------------------
  // Debug: guard state
  // -------------------------------------------------------
  useEffect(() => {
    if (!isDev) return;

    console.log(
      "[CampaignDashboard] guard snapshot",
      {
        user,
        profile,
        businessIdInt,
        authId,
        businessRow,
        loading,
      }
    );
  }, [
    user,
    profile,
    businessIdInt,
    authId,
    businessRow,
    loading,
  ]);

  // -------------------------------------------------------
  // Debug: dashboard state
  // -------------------------------------------------------
  useEffect(() => {
    if (!isDev) return;

    console.log(
      "[CampaignDashboard] state snapshot",
      {
        regionOverride,
        activeRegion,
        selectedFlyerId:
          selectedFlyer?.id ?? null,
        slotCount,
      }
    );
  }, [
    regionOverride,
    activeRegion,
    selectedFlyer,
    slotCount,
  ]);

  // -------------------------------------------------------
  // Handle Pro upgrade redirect
  // -------------------------------------------------------
  useEffect(() => {
    if (upgraded !== "true") {
      return;
    }

    toast({
      title: "Pro Plan Activated",
      description:
        "Full QR Analytics access is now active.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });

    navigate("/dashboard", {
      replace: true,
    });
  }, [
    upgraded,
    navigate,
    toast,
  ]);

  // -------------------------------------------------------
  // Load first flyer for selected map region
  // -------------------------------------------------------
  useEffect(() => {
    if (
      !activeRegion ||
      activeRegion === "UK"
    ) {
      setSelectedFlyer(null);
      setFlyerLoading(false);
      return;
    }

    let cancelled = false;

    const loadFlyer =
      async () => {
        setFlyerLoading(true);
        setSelectedFlyer(null);

        if (isDev) {
          console.log(
            "[CampaignDashboard] loading flyer for region",
            activeRegion
          );
        }

        const {
          data,
          error,
        } = await supabase
          .from("flyers")
          .select(`
            id,
            region,
            postcode_sector,
            status
          `)
          .eq(
            "region",
            activeRegion
          )
          .order(
            "postcode_sector",
            {
              ascending: true,
            }
          )
          .limit(1);

        if (cancelled) {
          return;
        }

        if (error) {
          console.error(
            "[CampaignDashboard] Failed to load flyer:",
            error.message
          );

          toast({
            title:
              "Failed to load campaign",
            description:
              error.message ||
              "The campaign could not be loaded.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });

          setSelectedFlyer(null);
          setFlyerLoading(false);
          return;
        }

        if (
          data &&
          data.length > 0
        ) {
          setSelectedFlyer(
            data[0]
          );

          if (isDev) {
            console.log(
              "[CampaignDashboard] flyer loaded",
              data[0]
            );
          }
        } else {
          setSelectedFlyer(null);

          if (isDev) {
            console.log(
              "[CampaignDashboard] no flyer found for region",
              activeRegion
            );
          }
        }

        setFlyerLoading(false);
      };

    loadFlyer();

    return () => {
      cancelled = true;
    };
  }, [
    activeRegion,
    toast,
  ]);

  // -------------------------------------------------------
  // Region change received from map search/navigation
  // -------------------------------------------------------
  const handleRegionChange = (
    value
  ) => {
    const cleanRegion =
      String(value || "")
        .replace("_", "")
        .trim()
        .toUpperCase();

    if (
      !cleanRegion ||
      cleanRegion === "UK"
    ) {
      return;
    }

    if (isDev) {
      console.log(
        "[CampaignDashboard] map region changed",
        cleanRegion
      );
    }

    setRegionOverride(
      (previousRegion) => {
        const cleanPrevious =
          String(
            previousRegion || ""
          )
            .replace("_", "")
            .toUpperCase();

        return cleanPrevious ===
          cleanRegion
          ? `${cleanRegion}_`
          : cleanRegion;
      }
    );
  };

  // -------------------------------------------------------
  // Credit top-up
  // -------------------------------------------------------
  const handleTopUp =
    async () => {
      if (!authId) {
        toast({
          title:
            "Business account unavailable",
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      const {
        data,
        error,
      } =
        await supabase
          .functions
          .invoke(
            "topup-credit",
            {
              body: {
                businessAuthId:
                  authId,
                amount: 20,
              },
            }
          );

      if (error) {
        console.error(
          "[CampaignDashboard] Failed to start top-up:",
          error.message
        );

        toast({
          title:
            "Failed to start top-up",
          description:
            error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      if (!data?.url) {
        toast({
          title:
            "Top-up unavailable",
          description:
            "A checkout link was not returned.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      window.location.href =
        data.url;
    };

  // -------------------------------------------------------
  // Loading
  // -------------------------------------------------------
  if (
    loading &&
    !profile
  ) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        direction="column"
        gap={3}
        bg="white"
      >
        <Spinner
          color={BRAND_RED}
          thickness="3px"
        />

        <Text
          color="gray.600"
          fontWeight="semibold"
        >
          Loading dashboard…
        </Text>
      </Flex>
    );
  }

  // -------------------------------------------------------
  // Authentication guard
  // -------------------------------------------------------
  if (
    !user ||
    !profile
  ) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        direction="column"
        gap={4}
        px={4}
        textAlign="center"
        bg="white"
      >
        <Heading
          size="md"
          color={BRAND_RED}
        >
          Login required
        </Heading>

        <Text color="gray.600">
          Please log in to access
          the campaign dashboard.
        </Text>

        <Button
          bg={BRAND_RED}
          color="white"
          _hover={{
            bg: BRAND_RED_DARK,
          }}
          onClick={() =>
            navigate("/login")
          }
        >
          Go to Login
        </Button>
      </Flex>
    );
  }

  return (
    <Flex
      minH="100vh"
      bg={BRAND_RED_LIGHT}
      direction={{
        base: "column",
        md: "row",
      }}
      overflowX="hidden"
    >
      {/* MOBILE SIDEBAR DRAWER */}
      <Drawer
        isOpen={sidebarOpen}
        placement="left"
        onClose={() =>
          setSidebarOpen(false)
        }
      >
        <DrawerOverlay />

        <DrawerContent
          borderRightWidth="4px"
          borderRightColor={
            BRAND_RED
          }
        >
          <DrawerCloseButton
            color={BRAND_RED}
          />

          <Box
            pt={12}
            overflowY="auto"
          >
            <DashboardSidebar
              flyerId={
                selectedFlyer?.id
              }
              groupId={null}
              slotCount={slotCount}
              businessId={
                businessRow
              }
            />
          </Box>
        </DrawerContent>
      </Drawer>

      {/* DESKTOP SIDEBAR */}
      <Box
        width="240px"
        flexShrink={0}
        p={6}
        bg="white"
        borderRightWidth="1px"
        borderRightColor={
          BRAND_BORDER
        }
        display={{
          base: "none",
          md: "block",
        }}
        overflowY="auto"
      >
        <DashboardSidebar
          flyerId={
            selectedFlyer?.id
          }
          groupId={null}
          slotCount={slotCount}
          businessId={
            businessRow
          }
        />
      </Box>

      {/* MAIN CONTENT */}
      <Box
        flex="1"
        minWidth={0}
        width="100%"
        p={{
          base: 3,
          sm: 4,
          md: 6,
        }}
        overflowX="hidden"
      >
        <Flex
          direction="column"
          gap={{
            base: 4,
            md: 6,
          }}
          width="100%"
          maxWidth="1600px"
          mx="auto"
        >
          {/* MOBILE MENU */}
          <Button
            display={{
              base: "inline-flex",
              md: "none",
            }}
            onClick={() =>
              setSidebarOpen(true)
            }
            bg="white"
            color={BRAND_RED}
            borderWidth="1px"
            borderColor={
              BRAND_RED
            }
            _hover={{
              bg: BRAND_RED_LIGHT,
            }}
            alignSelf="flex-start"
            boxShadow="sm"
          >
            ☰ Menu
          </Button>

          <RedemptionToastListener />

          <NotificationToastListener />

          <CountdownWatcher
            toast={toast}
          />

          {/* BRAND HEADER */}
          <Flex
            justify="space-between"
            align={{
              base: "stretch",
              lg: "center",
            }}
            direction={{
              base: "column",
              lg: "row",
            }}
            gap={4}
            bg="white"
            borderRadius="xl"
            borderWidth="1px"
            borderColor={
              BRAND_BORDER
            }
            boxShadow="sm"
            overflow="hidden"
          >
            <Flex
              align="center"
              gap={3}
              minWidth={0}
              p={{
                base: 4,
                md: 6,
              }}
            >
              <BrandLogo
                size={34}
              />

              <Box minWidth={0}>
                <Heading
                  color={BRAND_RED}
                  fontSize={{
                    base: "xl",
                    md: "2xl",
                  }}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  Postcode.Coupons
                </Heading>

                <Text
                  fontSize="sm"
                  color="gray.500"
                  mt={1}
                >
                  Campaign Cockpit
                </Text>
              </Box>
            </Flex>

            <Flex
              align="stretch"
              direction={{
                base: "column",
                sm: "row",
              }}
              gap={0}
              width={{
                base: "100%",
                lg: "auto",
              }}
              alignSelf="stretch"
            >
              {/* CREDIT CARD */}
              <Flex
                direction="column"
                justify="center"
                minWidth={{
                  lg: "170px",
                }}
                bg={BRAND_RED}
                color="white"
                px={6}
                py={4}
                textAlign={{
                  base: "left",
                  sm: "center",
                }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  opacity={0.9}
                >
                  Credit balance
                </Text>

                <Text
                  fontSize="2xl"
                  lineHeight="1.1"
                  fontWeight="extrabold"
                  mt={1}
                >
                  £
                  {Number(
                    profile?.credit_balance ||
                      0
                  ).toFixed(2)}
                </Text>

                <Button
                  size="sm"
                  mt={3}
                  bg="white"
                  color={BRAND_RED}
                  borderWidth="1px"
                  borderColor="white"
                  _hover={{
                    bg: BRAND_RED_LIGHT,
                  }}
                  onClick={
                    handleTopUp
                  }
                >
                  Top Up Credit
                </Button>
              </Flex>

              {/* LOGOUT AREA */}
              <Flex
                align="center"
                justify="center"
                bg="white"
                px={5}
                py={4}
                borderLeftWidth={{
                  base: 0,
                  sm: "1px",
                }}
                borderTopWidth={{
                  base: "1px",
                  sm: 0,
                }}
                borderColor={
                  BRAND_BORDER
                }
              >
                <LogoutButton />
              </Flex>
            </Flex>
          </Flex>

          {/* PRO BANNER */}
          {profile?.plan ===
            "pro" && (
            <Alert
              status="info"
              variant="solid"
              borderRadius="xl"
              bg={BRAND_RED}
              color="white"
              borderWidth="1px"
              borderColor={
                BRAND_RED_DARK
              }
              boxShadow="sm"
              px={{
                base: 4,
                md: 5,
              }}
              py={3}
            >
              <AlertIcon
                color="white"
              />

              <Flex
                align={{
                  base:
                    "flex-start",
                  sm: "center",
                }}
                justify="space-between"
                width="100%"
                direction={{
                  base: "column",
                  sm: "row",
                }}
                gap={2}
              >
                <Box>
                  <AlertTitle
                    fontSize={{
                      base: "sm",
                      md: "md",
                    }}
                  >
                    Pro Plan Active
                  </AlertTitle>

                  <Text
                    fontSize="sm"
                    opacity={0.9}
                  >
                    Full QR Analytics
                    access is enabled.
                  </Text>
                </Box>

                <Badge
                  bg="white"
                  color={BRAND_RED}
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontWeight="extrabold"
                >
                  PRO
                </Badge>
              </Flex>
            </Alert>
          )}

          {selectedFlyer && (
            <SlotCountWatcher
              businessId={
                businessIdInt
              }
              flyerId={
                selectedFlyer.id
              }
              setSlotCount={(
                count
              ) =>
                setSlotCount(
                  count
                )
              }
            />
          )}

          {/* MAP HERO */}
          <Box
            bg="white"
            p={{
              base: 2,
              sm: 3,
              md: 4,
            }}
            borderRadius="xl"
            boxShadow="md"
            borderWidth="1px"
            borderColor={
              BRAND_BORDER
            }
            overflow="hidden"
          >
            <Flex
              justify="space-between"
              align={{
                base: "flex-start",
                sm: "center",
              }}
              direction={{
                base: "column",
                sm: "row",
              }}
              gap={2}
              px={{
                base: 2,
                md: 3,
              }}
              pt={2}
              mb={2}
            >
              <Box>
                <Heading
                  size="md"
                  color={BRAND_RED}
                >
                  Explore Postcode
                  Sectors
                </Heading>

                <Text
                  fontSize="sm"
                  color="gray.500"
                  mt={1}
                >
                  Search or click a
                  sector to view its
                  flyer and available
                  coupons.
                </Text>
              </Box>

              {activeRegion && (
                <Badge
                  bg={BRAND_RED_LIGHT}
                  color={BRAND_RED}
                  borderWidth="1px"
                  borderColor={
                    BRAND_BORDER
                  }
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  Region{" "}
                  {activeRegion}
                </Badge>
              )}
            </Flex>

            <Box
              width="100%"
              minHeight={{
                base: "600px",
                md: "720px",
              }}
              overflow="hidden"
              borderRadius="lg"
            >
              <PostcodeCouponsMap
                authId={authId}
                flyerId={
                  selectedFlyer?.id ??
                  null
                }
                region={
                  activeRegion ||
                  "PR"
                }
                setRegionOverride={
                  handleRegionChange
                }
              />
            </Box>
          </Box>

          {/* CAMPAIGN DATA LOADING */}
          {flyerLoading && (
            <Flex
              align="center"
              justify="center"
              gap={3}
              py={5}
              bg="white"
              borderRadius="xl"
              borderWidth="1px"
              borderColor={
                BRAND_BORDER
              }
            >
              <Spinner
                size="sm"
                color={BRAND_RED}
              />

              <Text color="gray.600">
                Loading campaign data…
              </Text>
            </Flex>
          )}

          {/* CAMPAIGN INTELLIGENCE */}
          {!flyerLoading &&
            selectedFlyer && (
            <Box
              bg="white"
              p={{
                base: 3,
                md: 5,
              }}
              borderRadius="xl"
              boxShadow="sm"
              borderWidth="1px"
              borderColor={
                BRAND_BORDER
              }
              overflowX="hidden"
            >
              <Flex
                justify="space-between"
                align={{
                  base:
                    "flex-start",
                  sm: "center",
                }}
                direction={{
                  base: "column",
                  sm: "row",
                }}
                gap={2}
                mb={4}
              >
                <Box>
                  <Heading
                    size="md"
                    color={BRAND_RED}
                  >
                    Campaign
                    Intelligence
                  </Heading>

                  <Text
                    fontSize="sm"
                    color="gray.500"
                    mt={1}
                  >
                    Performance data
                    for the current
                    campaign.
                  </Text>
                </Box>

                <Button
                  size="sm"
                  bg={BRAND_RED}
                  color="white"
                  _hover={{
                    bg:
                      BRAND_RED_DARK,
                  }}
                  onClick={() =>
                    navigate(
                      `/flyer/${selectedFlyer.postcode_sector}`
                    )
                  }
                >
                  View Selected Flyer
                </Button>
              </Flex>

              <CampaignIntelModules
                businessId={
                  authId
                }
                flyerId={
                  selectedFlyer.id
                }
                groupId={null}
              />
            </Box>
          )}

          {/* DISPATCH */}
          {!flyerLoading &&
            selectedFlyer && (
            <Box
              bg="white"
              p={{
                base: 3,
                md: 5,
              }}
              borderRadius="xl"
              boxShadow="sm"
              borderWidth="1px"
              borderColor={
                BRAND_BORDER
              }
              overflowX="hidden"
            >
              <Heading
                size="md"
                color={BRAND_RED}
                mb={4}
              >
                Dispatch
              </Heading>

              <DispatchModules
                flyerId={
                  selectedFlyer.id
                }
                region={
                  activeRegion
                }
                group={null}
              />
            </Box>
          )}
        </Flex>
      </Box>
    </Flex>
  );
}